import { Link, useFetcher, useNavigate } from "@remix-run/react"
import { useEffect, useState } from "react"
// Type-only: `loader` is used solely as `typeof loader`. A value import pulls the server route
// (and its session/prisma chain) into this client module at runtime.
import type { loader } from "~/routes/_app+/rentals"
import { Button } from "~/components/ui/button"
import { CubeIcon, ExclamationTriangleIcon, PlayIcon } from "@radix-ui/react-icons"
import { Packages, UserPayment, UserSubscription, Users, Videos } from "@prisma/client"
import { Purchase } from "~/components/purchase"
import { ConfirmDialog, SuccessDialog } from "~/components/dialogs"
import Typography from "~/components/typography"
import { Label } from "~/components/ui/label"
import { Input } from "~/components/ui/input"
import { Textarea } from "~/components/ui/textarea"
import { Alert, AlertDescription } from "~/components/ui/alert"
import { Drawer, DrawerContent } from "~/components/ui/drawer"
import { Separator } from "~/components/ui/separator"
import { Title } from "~/components/header"
import { Card, CardFooter, CardHeader, CardTitle } from "~/components/ui/card"
import { dotCart } from "~/utils/trackers/dot"
import { useCookies } from "react-cookie"
import { isModernPricing, pricingSuffix } from "~/utils/pricing"
// The hardcoded plan lists live in a pure data module so they can be imported (and unit-tested)
// without dragging this component's server-route dependency chain along. Re-exported here because
// several routes still import them from "~/components/rentals".
import { packages, packagesV2, packagesV3, packagesV4, packagesBySuffix } from "~/utils/pricing-packages"

export { packages, packagesV2, packagesV3, packagesV4, packagesBySuffix }
export type { PricingPackage } from "~/utils/pricing-packages"

export function RentalButton({
  text,
  user,
  sub,
  video,
  source,
  sourceID,
  starName,
  rentExpiry,
  pkg,
  pkgs,
  cards,
  credits,
  pricingCohort,
  open,
  setOpen,
}: {
  text?: string
  user: Users | null
  sub: boolean
  video: Videos
  source?: string
  sourceID?: number
  starName: string
  rentExpiry: any
  pkg: Packages
  pkgs: any
  cards: UserPayment[]
  credits: number
  pricingCohort?: string | null
  open: boolean
  setOpen: (open: boolean) => void
}) {
  const fetcher = useFetcher<typeof loader>()
  const navigate = useNavigate()
  const [selectedPkg, setSelectedPkg] = useState<Packages | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [confirmRental, setConfirmRental] = useState(false)
  const [rented, setRented] = useState(!!rentExpiry)
  const [openPurchase, setOpenPurchase] = useState(false)
  const [openSubscribe, setOpenSubscribe] = useState(false)
  const [openRecharge, setOpenRecharge] = useState(false)
  const rechargeFetcher = useFetcher<{ rechargeSuccess?: boolean; rechargeError?: string }>()
  const [cookies, setCookie, removeCookie] = useCookies<string>(["cart"])
  const buttonTxt = text || "Rent"

  useEffect(() => {
    setSubmitting(fetcher.state == "submitting")
  }, [fetcher.state])

  useEffect(() => {
    setRented(!!rentExpiry)
    setOpenSubscribe(false)
  }, [rentExpiry])

  useEffect(() => {
    setConfirmRental(false)

    if (fetcher.data) {
      setRented(!!fetcher.data.rental)
    }
  }, [fetcher.data])

  function openConfirm(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault()
    setConfirmRental(true)
  }

  function handleConfirm() {
    const formData = new FormData()
    formData.append("VideoID", video.VideoID.toString())
    if (source) {
      formData.append("Source", source)
      formData.append("SourceID", sourceID!.toString())
    }
    fetcher.submit(formData, { action: `/rentals`, method: "post" })
  }

  function purchase(pkgTag: string) {
    if (!user) {
      navigate("/login?redirectTo=/subscribe")
    }
    setSelectedPkg(pkgs[pkgTag])
    setOpenPurchase(true)
  }

  // Recharge is a STRUCTURAL property of modern pricing (see ~/utils/pricing), so it must follow
  // isModernPricing, not a `=== "test"` sniff — the sniff sent v3 subscribers down the legacy
  // purchase branch instead of the recharge flow.
  const rechargeOnRent = isModernPricing({ PricingCohort: pricingCohort })

  function rent() {
    if (sub && rechargeOnRent) {
      setOpenRecharge(true)
    } else if (sub) {
      setSelectedPkg(pkg)
      setOpenPurchase(true)
    } else {
      setOpenSubscribe(true)
    }

    dotCart({ cookies, setCookie, removeCookie, pkg, video })
  }

  useEffect(() => {
    if (open) {
      if (sub && rechargeOnRent) {
        setOpenRecharge(true)
      } else if (sub) {
        setSelectedPkg(pkg)
        setOpenPurchase(true)
      } else {
        setOpenSubscribe(true)
      }
    } else {
      setOpenPurchase(false)
      setOpenSubscribe(false)
      setOpenRecharge(false)
    }
  }, [open])

  useEffect(() => {
    if (!openPurchase && !openSubscribe && !openRecharge) {
      setOpen(false)
    }
  }, [openPurchase, openSubscribe, openRecharge])

  return (
    <>
      {credits > 0 || rented ?
        <Button className="px-4 py-4 text-md" disabled={submitting || rented} onClick={openConfirm}>
          <PlayIcon className="mr-2 h-4 w-4" />
          {submitting ?
            "Renting..."
          : rented ?
            "Rented"
          : buttonTxt}
        </Button>
      : <Button className="px-4 py-4 text-md" onClick={rent}>
          <PlayIcon className="mr-2 h-4 w-4" />
          {buttonTxt}
        </Button>
      }
      <Drawer open={openSubscribe} onOpenChange={setOpenSubscribe}>
        <DrawerContent className="z-30">
          <div className="mx-4 pt-0 sm:pt-4">
            <div className="sm:pt-2 md:mx-6">
              <div className={"flex flex-wrap items-center justify-center md:space-x-4 space-y-4"}>
                <Title
                  content="Enjoy as much #RealWorldSex as you choose:"
                  className="text-md sm:text-xl md:text-4xl mt-4 sm:mt-0 text-center"
                />
              </div>
              <Separator className="my-4" />
              <Typography className="mb-8 mx-4 md:mx-16 text-center max-w-none">
                <h4 className="text-xs sm:text-lg">
                  <span className="font-light">
                    This video is yours for 4 weeks!
                    <br className="sm:hidden" /> Half of what you pay goes to
                  </span>{" "}
                  {starName}
                </h4>
              </Typography>
              <section className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-2 md:gap-8">
                {/* Suffix-selected, not `pricingCohort === "test"`: that sniff sent a v3 viewer to
                    the LEGACY list. Wrapping the cohort in a user-shaped object keeps the resolver
                    the single source of truth — note an absent cohort still means legacy here. */}
                {packagesBySuffix[pricingSuffix({ PricingCohort: pricingCohort })].monthly.map((pkg) => {
                  return <MinimalPricingCard key={pkg.title} {...pkg} onPurchase={purchase} />
                })}
              </section>
              <Typography className="mt-12 mb-12 mx-4 md:mx-16 text-center max-w-none">
                <h3 className="text-lg sm:text-xl">Not quite ready for a subscription?</h3>
                <p className="mb-1 text-sm sm:text-md">
                  Pay as you go and{" "}
                  <Link
                    to="/welcome-bundle"
                    onClick={(e) => {
                      setSelectedPkg(pkg)
                      setOpenPurchase(true)
                      e.preventDefault()
                    }}>
                    rent this one video for ${Number(pkg.Price)}
                  </Link>
                </p>
                <p className="mt-4 mb-1 text-xs sm:text-sm text-muted-foreground">
                  Read more about our <Link to="/subscribe">subscription plans</Link>
                </p>
              </Typography>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
      <Purchase
        pkg={selectedPkg}
        video={video}
        source={source}
        sourceID={sourceID}
        description={selectedPkg === pkg ? `Rent video "${video.VideoTitle}" for 4 weeks` : undefined}
        cards={cards}
        open={openPurchase}
        setOpen={setOpenPurchase}
        setRented={setRented}
      />
      <ConfirmDialog
        open={confirmRental}
        onOpenChange={setConfirmRental}
        submitting={submitting}
        title="Rent Video for 1 Credit"
        content={`You currently have ${credits} available credits`}
        onConfirm={handleConfirm}
      />
      <Drawer open={openRecharge} onOpenChange={(o) => { if (!rechargeFetcher.state || rechargeFetcher.state === "idle") setOpenRecharge(o) }}>
        <DrawerContent className="z-30">
          <div className="mx-4 pt-0 sm:pt-4">
            <div className="sm:pt-2 md:mx-6 pb-8">
              {rechargeFetcher.data?.rechargeSuccess ?
                <>
                  <Typography className="text-center max-w-none mt-4">
                    <h3>You're recharged!</h3>
                    <p>Your credits have been refreshed. Enjoy the video.</p>
                  </Typography>
                  <div className="flex justify-center mt-4">
                    <Button
                      onClick={() => {
                        setOpenRecharge(false)
                        setConfirmRental(true)
                      }}>
                      Rent Now
                    </Button>
                  </div>
                </>
              : <>
                  <Typography className="text-center max-w-none mt-4">
                    <h3>You're out of credits</h3>
                    <p>
                      Recharge your subscription to keep watching. You'll be charged your monthly amount immediately and
                      receive a full set of fresh credits — then your subscription resets and renews again in one month.
                    </p>
                    {rechargeFetcher.data?.rechargeError && (
                      <p className="text-destructive text-sm">
                        <strong>Error:</strong> {rechargeFetcher.data.rechargeError}
                      </p>
                    )}
                  </Typography>
                  <div className="flex justify-center mt-4">
                    <Button
                      disabled={rechargeFetcher.state === "submitting"}
                      onClick={() => {
                        const formData = new FormData()
                        formData.set("_action", "recharge")
                        rechargeFetcher.submit(formData, { action: "/billing", method: "post" })
                      }}>
                      {rechargeFetcher.state === "submitting" ? "Recharging..." : "Recharge Subscription"}
                    </Button>
                  </div>
                </>
              }
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  )
}

type PricingCardProps = {
  title: string
  tag: string
  pkgRentals: number
  pkgDesc: string
  monthlyPrice?: number
  actionLabel: string
  onPurchase: (pkgTag: string) => void
}

const MinimalPricingCard = ({
  title,
  tag,
  pkgRentals,
  pkgDesc,
  monthlyPrice,
  actionLabel,
  onPurchase,
}: PricingCardProps) => {
  return (
    <>
      <Card className="w-full flex justify-between items-center border-accent p-0 mx-auto md:hidden">
        <CardHeader className="p-2 px-3 space-y-1">
          <CardTitle className="text-md">{title}</CardTitle>
          <div className="text-sm">
            {pkgRentals} rentals for ${monthlyPrice}/month
          </div>
        </CardHeader>
        <CardFooter className="p-2 px-3">
          <Button
            className="rounded-lg bg-primary text-primary-foreground px-2 h-10 m-0 font-sm"
            onClick={() => onPurchase(tag)}>
            {actionLabel}
          </Button>
        </CardFooter>
      </Card>
      <Card className="hidden md:w-72 md:flex md:flex-col justify-between py-1 border-accent mx-auto md:mx-0 hover:scale-110 transition-transform">
        <div>
          <CardHeader className="pb-8 pt-4">
            <CardTitle className="text-lg">{title}</CardTitle>
            <div className="flex gap-0.5">
              <h3 className="text-3xl font-bold">{"$" + monthlyPrice}</h3>
              <span className="flex flex-col justify-end text-sm mb-1">/month</span>
            </div>
            <div className="text-sm">{pkgDesc}</div>
          </CardHeader>
        </div>
        <CardFooter className="mt-2">
          <Button
            className="w-full rounded-md bg-primary text-primary-foreground px-6 font-medium"
            onClick={() => onPurchase(tag)}>
            {actionLabel}
          </Button>
        </CardFooter>
      </Card>
    </>
  )
}

export function GiftButton({
  video,
  pkg,
  cards,
  credits,
}: {
  video: Videos
  pkg: Packages
  cards: UserPayment[]
  credits: number
}) {
  const fetcher = useFetcher<typeof loader>()
  const [submitting, setSubmitting] = useState(false)
  const [confirmGift, setConfirmGift] = useState(false)
  const [success, setSuccess] = useState(false)
  const [giftOpen, setGiftOpen] = useState(false)
  const [assignableContact, setAssignableContact] = useState("")
  const [assignableNote, setAssignableNote] = useState("")
  const [error, setError] = useState("")
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})
  const [credit, setCredit] = useState(null)

  useEffect(() => {
    setSubmitting(fetcher.state == "submitting")
  }, [fetcher.state])

  function openGiftConfirmOrPurchase(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault()
    if (credits > 0) {
      setConfirmGift(true)
    } else {
      setGiftOpen(true)
    }
  }

  useEffect(() => {
    if (fetcher.data?.error) {
      setError(fetcher.data.error)
    } else if (fetcher.data?.fieldErrors) {
      setFieldErrors(fetcher.data.fieldErrors)
    } else {
      setError("")
      setFieldErrors({})
    }

    if (fetcher.data?.credit) {
      setConfirmGift(false)
      setSuccess(true)
      setCredit(fetcher.data.credit)
    }
  }, [fetcher.data])

  function handleGiftConfirm() {
    const formData = new FormData()
    formData.append("VideoID", video.VideoID.toString())
    formData.append("AssignableContact", assignableContact)
    formData.append("AssignableNote", assignableNote)
    fetcher.submit(formData, { action: "/rentals", method: "post" })
  }

  return (
    <>
      <Button variant="outline" className="border-foreground/30" onClick={openGiftConfirmOrPurchase}>
        <CubeIcon className="mr-2 h-4 w-4" /> Gift
      </Button>
      <SuccessDialog
        open={success}
        onOpenChange={setSuccess}
        title="You've successfully gifted this video"
        content={
          <Typography>
            <p>
              <b>{credit?.AssignableContact!}</b> will receive an email shortly to redeem your gift of #RealWorldSex.
            </p>
          </Typography>
        }
      />
      <ConfirmDialog
        open={confirmGift}
        onOpenChange={setConfirmGift}
        submitting={submitting}
        title="Gift Video for 1 credit"
        content={
          <>
            {error && (
              <Alert variant="destructive">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Typography>
              <p>You currently have {credits} available credits</p>
            </Typography>
            <div className="space-y-2">
              <div className="space-y-2">
                <Label className="mx-1" htmlFor="AssignableContact">
                  Recipients Email Address
                </Label>
                <div className="relative">
                  <Input
                    type="text"
                    id="AssignableContact"
                    name="AssignableContact"
                    value={assignableContact}
                    onChange={(e) => setAssignableContact(e.target.value)}
                    className="mt-4"
                  />
                </div>
                {fieldErrors["AssignableContact"] && (
                  <span className="text-[0.8rem] font-medium text-destructive">{fieldErrors["AssignableContact"]}</span>
                )}
              </div>
              <div className="space-y-2">
                <Label className="mx-1" htmlFor="AssignableNote">
                  Personal Note <span className="text-muted-foreground">(optional)</span>
                </Label>
                <div className="relative">
                  <Textarea
                    id="AssignableNote"
                    name="AssignableNote"
                    value={assignableNote}
                    onChange={(e) => setAssignableNote(e.target.value)}
                  />
                </div>
                {fieldErrors["AssignableNote"] && (
                  <span className="text-[0.8rem] font-medium text-destructive">{fieldErrors["AssignableNote"]}</span>
                )}
              </div>
            </div>
          </>
        }
        onConfirm={handleGiftConfirm}
      />
      <Purchase
        pkg={pkg}
        video={video}
        gifting={true}
        description={`Gifting a rental of video "${video.VideoTitle}" for 4 weeks`}
        cards={cards}
        open={giftOpen}
        setOpen={setGiftOpen}
      />
    </>
  )
}
